import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Zap, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { usePermission } from "@/contexts/RoleAccessContext";
import { createCanHelpers } from "@/utils/rbac";
import { assignAppraisal } from "@/services/performanceService";

/**
 * Self-contained "One-Time Review" dialog (trigger button + form). Owns its own
 * form state so typing only re-renders this component, not the host page.
 *
 * Props:
 *  - employeeList: [{ id, name }]
 *  - templates: [{ _id|id, name, type }]
 *  - onCreated: () => void — called after a review is created (e.g. to reload data)
 */
export default function OneTimeReviewDialog({
  employeeList = [],
  templates = [],
  onCreated,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  hideTrigger = false,
}) {
  const navigate = useNavigate();
  const { can } = usePermission();
  // Memoize so typing doesn't re-invoke can() on every keystroke.
  const { canCreate } = useMemo(() => createCanHelpers(can), [can]);
  const canCreateAppraisal = useMemo(() => canCreate("performance_management"), [canCreate]);

  // Controlled when a parent passes `open`/`onOpenChange` (e.g. driven from the
  // Performance "Actions" dropdown); otherwise self-managed with its own trigger.
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = (next) => {
    if (!isControlled) setInternalOpen(next);
    controlledOnOpenChange?.(next);
  };
  const [oneTimeEmployeeId, setOneTimeEmployeeId] = useState("");
  const [oneTimeTemplateId, setOneTimeTemplateId] = useState("");
  const [oneTimeCycleName, setOneTimeCycleName] = useState("");
  const [oneTimeDeadline, setOneTimeDeadline] = useState("");
  const [employeeSearchQuery, setEmployeeSearchQuery] = useState("");
  const [assigning, setAssigning] = useState(false);

  const resetForm = () => {
    setOneTimeEmployeeId("");
    setOneTimeTemplateId("");
    setOneTimeCycleName("");
    setOneTimeDeadline("");
    setEmployeeSearchQuery("");
  };

  const handleOpenChange = (next) => {
    setOpen(next);
    if (!next) resetForm();
  };

  const handleAssignAppraisal = async () => {
    if (!canCreate("performance_management", true)) return;

    if (!oneTimeEmployeeId || !oneTimeTemplateId || !oneTimeCycleName.trim() || !oneTimeDeadline) {
      toast.error("Please select employee, template, and provide a review name and deadline");
      return;
    }

    const todayMid = new Date();
    todayMid.setHours(0, 0, 0, 0);
    const deadlineDate = new Date(oneTimeDeadline);
    deadlineDate.setHours(0, 0, 0, 0);
    if (deadlineDate < todayMid) {
      toast.error("Deadline cannot be in the past");
      return;
    }

    try {
      setAssigning(true);
      const response = await assignAppraisal(
        oneTimeEmployeeId,
        oneTimeTemplateId,
        oneTimeCycleName,
        oneTimeDeadline
      );
      toast.success(response.message || "Appraisal assigned successfully!");
      setOpen(false);
      resetForm();
      onCreated?.();
    } catch (error) {
      console.error("Error assigning appraisal:", error);
      toast.error(error.response?.data?.error || error.response?.data?.message || "Failed to assign appraisal");
    } finally {
      setAssigning(false);
    }
  };

  const filteredEmployees = employeeList.filter(
    (emp) =>
      emp.name.toLowerCase().includes(employeeSearchQuery.toLowerCase()) ||
      emp.id.toLowerCase().includes(employeeSearchQuery.toLowerCase())
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {!hideTrigger && (
        <DialogTrigger asChild>
          <Button
            variant="default"
            size="sm"
            className={`bg-primary hover:bg-primary/90 ${
              !canCreateAppraisal ? "opacity-70 cursor-not-allowed" : ""
            }`}
            disabled={!canCreateAppraisal}
            data-testid="one-time-review-btn"
          >
            <Zap className="mr-1.5 h-4 w-4" /> One-Time Review
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Initiate One-Time Appraisal</DialogTitle>
          <DialogDescription>
            Conduct an off-cycle review for a specific employee.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label>Review Name *</Label>
            <Input
              placeholder="e.g., Q1 Special Review"
              value={oneTimeCycleName}
              onChange={(e) => setOneTimeCycleName(e.target.value)}
              data-testid="one-time-cycle-name"
              required
            />
          </div>
          <div className="grid gap-2">
            <Label>Select Employee *</Label>
            <Select
              value={oneTimeEmployeeId}
              onValueChange={(value) => {
                setOneTimeEmployeeId(value);
                setEmployeeSearchQuery("");
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select employee..." />
              </SelectTrigger>
              <SelectContent>
                <div className="p-2" onClick={(e) => e.stopPropagation()}>
                  <Input
                    placeholder="Search employee..."
                    value={employeeSearchQuery}
                    onChange={(e) => {
                      e.stopPropagation();
                      setEmployeeSearchQuery(e.target.value);
                    }}
                    onKeyDown={(e) => e.stopPropagation()}
                    className="h-8"
                  />
                </div>
                <div className="max-h-[300px] overflow-y-auto">
                  {filteredEmployees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.name}
                    </SelectItem>
                  ))}
                  {filteredEmployees.length === 0 && (
                    <div className="py-6 text-center text-sm text-muted-foreground">
                      No employee found.
                    </div>
                  )}
                </div>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label>Review Template *</Label>
            <Select value={oneTimeTemplateId} onValueChange={setOneTimeTemplateId}>
              <SelectTrigger>
                <SelectValue placeholder="Select template..." />
              </SelectTrigger>
              <SelectContent>
                {(() => {
                  const filteredTemplates = Array.isArray(templates)
                    ? templates.filter((t) => t.type === "Appraisal")
                    : [];

                  if (filteredTemplates.length === 0) {
                    return (
                      <div className="py-6 px-4 text-center">
                        <FileText className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground mb-2">
                          No Appraisal templates available
                        </p>
                        <Button
                          variant="link"
                          className="text-xs h-auto p-0"
                          onClick={() => navigate("/template-builder")}
                        >
                          Create template →
                        </Button>
                      </div>
                    );
                  }

                  return filteredTemplates.map((t) => (
                    <SelectItem key={t._id || t.id} value={t._id || t.id}>
                      {t.name}
                    </SelectItem>
                  ));
                })()}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label>Deadline *</Label>
            <Input
              type="date"
              value={oneTimeDeadline}
              onChange={(e) => setOneTimeDeadline(e.target.value)}
              min={new Date().toISOString().split("T")[0]}
              data-testid="one-time-deadline"
              required
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={handleAssignAppraisal}
            disabled={
              assigning ||
              !oneTimeEmployeeId ||
              !oneTimeTemplateId ||
              !oneTimeCycleName.trim() ||
              !oneTimeDeadline
            }
          >
            {assigning ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Assigning...
              </>
            ) : (
              "Create Review"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
